import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import PageHelmet from 'containers/PageHelmet';
import CurrentPageProvider from '../containers/CurrentPageProvider';
import SectionList from './SectionList';
import classes from './Page.scss';

const propTypes = {
  page: PropTypes.shape({
    metadata: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string.isRequired,
      titlePostfix: PropTypes.string,
      slug: PropTypes.string,
    }),
  }),
  version: PropTypes.shape({
    id: PropTypes.string,
    metadata: PropTypes.object,
    sections: PropTypes.array,
  }),
  layout: PropTypes.object,
  companyName: PropTypes.string.isRequired,
  revise: PropTypes.func.isRequired,
  saveVersion: PropTypes.func.isRequired,
  isSectionBeingEdited: PropTypes.func.isRequired,
  isWidgetBeingEdited: PropTypes.func.isRequired,
  handleWidgetEditStart: PropTypes.func.isRequired,
  handleWidgetEditStop: PropTypes.func.isRequired,
  handleSectionEditStart: PropTypes.func.isRequired,
  handleSectionEditStop: PropTypes.func.isRequired,
  showSectionPlaceholders: PropTypes.bool,
  pathname: PropTypes.string.isRequired,
  hostnames: PropTypes.object,
  onShowPopupModal: PropTypes.func.isRequired,
  onHidePopupModal: PropTypes.func.isRequired,
  sectionWidgetMoving: PropTypes.bool.isRequired,
  onStartWidgetSwitch: PropTypes.func.isRequired,
  onStopWidgetSwitch: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  widgetSwitchingId: PropTypes.string,
};

const defaultProps = {};

const Page = ({
  page,
  version,
  layout,
  companyName,
  revise,
  saveVersion,
  isSectionBeingEdited,
  isWidgetBeingEdited,
  handleWidgetEditStart,
  handleWidgetEditStop,
  handleSectionEditStart,
  handleSectionEditStop,
  showSectionPlaceholders,
  pathname,
  hostnames,
  onShowPopupModal,
  onHidePopupModal,
  sectionWidgetMoving,
  onStartWidgetSwitch,
  onStopWidgetSwitch,
  isSubmitting,
  widgetSwitchingId,
}) => {
  const builder = useRef();

  const handleDocumentClick = (e) => {
    if (!builder.current || builder.current.contains(e.target)) {
      return;
    }

    if (widgetSwitchingId) {
      onStopWidgetSwitch();
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener('click', handleDocumentClick);
    },
    [],
  );

  useEffect(() => {
    if (widgetSwitchingId) {
      window.addEventListener('click', handleDocumentClick);
      return;
    }

    window.removeEventListener('click', handleDocumentClick);
  }, [widgetSwitchingId]);

  const reviseSections = async (sections) => {
    const currentVersion = {
      ...version,
      sections,
      id: uuid.v4(), // Always give versions a new ID
    };

    await revise(currentVersion);
    saveVersion(page.id, currentVersion);
  };

  if (!page || !version) {
    return false;
  }

  return (
    <div className={classes.page} ref={builder}>
      <PageHelmet
        title={page.metadata.title}
        companyName={companyName}
        description={page.metadata.description}
        titlePostfix={page.metadata.titlePostfix}
        canonical={hostnames.fqdn ? `${hostnames.fqdn}${pathname}` : null}
        slug={page.metadata.title}
      />
      <CurrentPageProvider>
        <SectionList
          sections={version.sections}
          layout={layout}
          showPlaceholders={showSectionPlaceholders}
          isSectionBeingEdited={isSectionBeingEdited}
          isWidgetBeingEdited={isWidgetBeingEdited}
          onWidgetEditStart={handleWidgetEditStart}
          onWidgetEditStop={handleWidgetEditStop}
          onSectionEditStart={handleSectionEditStart}
          onSectionEditStop={handleSectionEditStop}
          onShowPopupModal={onShowPopupModal}
          onHidePopupModal={onHidePopupModal}
          onUpdate={reviseSections}
          pageType={page.metadata.type}
          sectionWidgetMoving={sectionWidgetMoving}
          onStartWidgetSwitch={onStartWidgetSwitch}
          facilityId={page.facilityId}
          locale={page.locale}
          isSubmitting={isSubmitting}
          legacyWidgetLoader
        />
      </CurrentPageProvider>
    </div>
  );
};

Page.propTypes = propTypes;
Page.defaultProps = defaultProps;

export default Page;
